import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import Loading from "../components/Loading";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./ValidarChave.module.css";
import { Eye, EyeOff, TriangleAlert } from "lucide-react";

type Etapa = "validando" | "valida" | "invalida";

const ValidarChave = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const email = searchParams.get("email") || "";
  const chave = searchParams.get("chave") || "";

  const [etapa, setEtapa] = useState<Etapa>("validando");
  const [senha, setSenha] = useState("");
  const [confirmaSenha, setConfirmaSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirma, setMostrarConfirma] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenAtivacao, setTokenAtivacao] = useState("");

  useEffect(() => {
    document.title = "Íkone | Ativação de Acesso";

    const validar = async () => {
      if (!email || !chave) {
        setEtapa("invalida");
        toast.error("Link inválido: parâmetros ausentes.");
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API}/api/Usuario/ValidarChave?chave=${encodeURIComponent(
            chave,
          )}&email=${encodeURIComponent(email)}`,
          { method: "GET" },
        );

        // CORREÇÃO: Lemos como texto puro, já que a API retorna o JWT diretamente na response
        const dataText = await response.text();

        if (!response.ok || dataText === "erro" || !dataText) {
          setEtapa("invalida");
          toast.error("Chave inválida ou expirada.");
          return;
        }

        setTokenAtivacao(dataText); // guarda o JWT puro
        setEtapa("valida");
      } catch (error) {
        console.error(error);
        setEtapa("invalida");
        toast.error("Erro ao validar a chave de acesso.");
      }
    };

    validar();
  }, [email, chave]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (senha.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres.");
      return;
    }

    if (senha !== confirmaSenha) {
      toast.error("As senhas não coincidem.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/api/Usuario/AtivarAcesso`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenAtivacao}`,
          },
          body: JSON.stringify({
            email: email,
            chave: chave,
            senha: senha,
          }),
        },
      );

      // CORREÇÃO AQUI: Lemos como texto puro para evitar o crash do JSON
      const dataText = await response.text();

      if (!response.ok) {
        toast.error(dataText || "Erro ao ativar acesso.");
        return;
      }

      // Se o status for OK (200), agora o fluxo segue corretamente
      toast.success("Senha definida com sucesso! Faça login para continuar.");
      navigate("/area-do-cliente");
    } catch (error) {
      console.error(error);
      toast.error("Ocorreu um erro ao definir a senha.");
    } finally {
      setIsLoading(false);
    }
  };

  if (etapa === "validando") {
    return (
      <div className={styles.wrapper}>
        <div className={styles.loadingBox}>
          <Loading isButton={false} corPredefinida color="primary" />
          <span className={styles.loadingText}>Validando seu acesso...</span>
        </div>
      </div>
    );
  }

  if (etapa === "invalida") {
    return (
      <div className={styles.wrapper}>
        <div className={styles.invalidCard}>
          <div className={styles.invalidIcon}>
            <TriangleAlert />
          </div>
          <h4 className={styles.invalidTitle}>Link inválido ou expirado</h4>
          <p className={styles.invalidText}>
            Solicite um novo link de ativação/reset de senha.
          </p>
          <button
            className={styles.secondaryBtn}
            onClick={() => navigate("/area-do-cliente")}
          >
            Voltar ao login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.card}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <h3 className={styles.title}>Defina sua senha</h3>
          <p className={styles.subtitle}>
            Crie uma senha segura para acessar sua conta
          </p>

          <div className={styles.fieldGroup}>
            <label htmlFor="senha" className={styles.label}>
              Nova senha
            </label>
            <div className={styles.inputWrapper}>
              <input
                type={mostrarSenha ? "text" : "password"}
                id="senha"
                placeholder="Digite a nova senha"
                className={styles.input}
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required
                minLength={6}
              />
              <button
                type="button"
                className={styles.toggleBtn}
                onClick={() => setMostrarSenha((v) => !v)}
                tabIndex={-1}
              >
                {mostrarSenha ? <EyeOff /> : <Eye />}
              </button>
            </div>
            <span className={styles.hint}>Mínimo de 6 caracteres</span>
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="confirmaSenha" className={styles.label}>
              Confirme a senha
            </label>
            <div className={styles.inputWrapper}>
              <input
                type={mostrarConfirma ? "text" : "password"}
                id="confirmaSenha"
                placeholder="Confirme a nova senha"
                className={styles.input}
                value={confirmaSenha}
                onChange={(e) => setConfirmaSenha(e.target.value)}
                required
                minLength={6}
              />
              <button
                type="button"
                className={styles.toggleBtn}
                onClick={() => setMostrarConfirma((v) => !v)}
                tabIndex={-1}
              >
                {mostrarConfirma ? <EyeOff /> : <Eye />}
              </button>
            </div>
          </div>

          <button
            disabled={isLoading}
            type="submit"
            className={styles.submitBtn}
          >
            {isLoading ? (
              <Loading isButton={true} corPredefinida color="white" />
            ) : (
              "Confirmar"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ValidarChave;
